import { useEffect, useState } from "react";
import { Box, Typography, styled } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import IotVisionIconButton from "components/common/IotVisionIconButton";

type LiveDotColor = "success" | "warning" | "error";

const LiveDot = styled("span", {
    shouldForwardProp: (prop) => prop !== "color",
})<{ color: LiveDotColor }>(({ theme, color }) => ({
    display: "inline-block",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: theme.palette[color].main,
    marginRight: "6px",
}));

/**
 * A refresh is only "on schedule" until roughly the next poll was due; past that (tab was
 * hidden, a background poll silently failed, etc.) the data is stale even though no error was
 * ever surfaced. Multiplied rather than exact so a refresh landing a few seconds late doesn't
 * immediately flip the indicator.
 */
const STALE_MULTIPLIER = 2;

interface ILiveRefreshStatusProps {
    lastRefreshedAt: Date | null;
    intervalSeconds?: number;
    locale?: string;
    paused?: boolean;
    pausedMessage?: string;
    onRefresh?: () => void;
    refreshing?: boolean;
    /** Most recent fetch attempt failed - takes priority over staleness. */
    error?: boolean;
}

const LiveRefreshStatus = ({
    lastRefreshedAt,
    intervalSeconds = 60,
    locale = "en-AU",
    paused = false,
    pausedMessage = "Paused — return to the first page to resume",
    onRefresh,
    refreshing = false,
    error = false,
}: ILiveRefreshStatusProps): JSX.Element => {
    // Ticks once a second purely to re-evaluate staleness against the clock - lastRefreshedAt
    // itself doesn't change between polls, so without this the dot would only ever update at
    // the moment of a fetch, never while data quietly ages past the poll interval.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (paused) return undefined;
        const id = setInterval(() => setNow(Date.now()), 1_000);
        return () => clearInterval(id);
    }, [paused]);

    const ageSeconds = lastRefreshedAt ? (now - lastRefreshedAt.getTime()) / 1000 : null;
    const isStale = ageSeconds === null || ageSeconds > intervalSeconds * STALE_MULTIPLIER;

    let dotColor: LiveDotColor = "success";
    if (error) dotColor = "error";
    else if (paused || isStale) dotColor = "warning";

    const timeLabel = lastRefreshedAt ? lastRefreshedAt.toLocaleTimeString(locale, { hour12: false }) : "—";
    let statusMessage = `Live · last refresh ${timeLabel} · auto every ${intervalSeconds}s`;
    if (paused) statusMessage = pausedMessage;
    else if (error) statusMessage = `Couldn't refresh · last successful ${timeLabel}`;
    else if (isStale) statusMessage = `Data may be out of date · last refresh ${timeLabel}`;

    return (
        <Box display="flex" alignItems="center">
            <LiveDot color={dotColor} />
            <Typography variant="body2" color="text.secondary">
                {statusMessage}
            </Typography>
            {onRefresh ? (
                <Box sx={{ ml: 1 }}>
                    <IotVisionIconButton
                        icon={<RefreshIcon fontSize="small" />}
                        tooltip="Refresh now"
                        disabled={refreshing}
                        onClick={onRefresh}
                    />
                </Box>
            ) : null}
        </Box>
    );
};

export default LiveRefreshStatus;
